import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { getTextsData } from "selectors/texts";
import { useDispatch, useSelector } from "react-redux";
import Activity from "nlib/common/Activity";
import AwaitingButton from "nlib/common/AwaitingButton";
import CommentTargetTypes from "const/CommentTargetTypes";
import CommentsActions from "actions/CommentsActions";
import DataStatuses from "const/DataStatuses";
import DocumentPaymentTypes from "const/DocumentPaymentTypes";
import DocumentPreview from "mlib/common/DocumentPreview";
import DocumentRecogniseStatuses from "const/DocumentRecogniseStatuses";
import DocumentTypes from "const/DocumentTypes";
import EventsActions from "actions/EventsActions";
import Files from "utils/Files";
import FormLabel from "nlib/common/FormLabel";
import MainApiActions from "actions/MainApiActions";
import MainApiRoutes from "const/MainApiRoutes";
import Preloader from "nlib/common/Preloader";
import React, { useCallback, useLayoutEffect, useState } from "react";
import SideBar, { SideBarContent, SideBarHeader } from "nlib/common/SideBar";
import classNames from "classnames";
import usePreventBodyScroll from "hooks/usePreventBodyScroll";

const ListItemDetails = ({ documentId, data, onClose }) => {
  const {
    type,
    status,
    comments,
    paymentType,
    attachment = {},
    originalDocumentId,
    duplicatedDocumentsIds,
    attachment: { key: attachmentKey } = {},
    recogniseData: { status: recogniseStatus, previewFile, previewPageMeta } = {}
  } = data || {};

  usePreventBodyScroll();

  const dispatch = useDispatch();

  const { uiTexts } = useSelector(getTextsData);

  const [activityLoaded, setActivityLoaded] = useState(false);

  const extractionStatus = status === DataStatuses.TO_EXTRACT;

  const billType = paymentType === DocumentPaymentTypes.BUY && type === DocumentTypes.INVOICE;

  const typeContent = (paymentType && type && (billType ? DocumentTypes.BILL : type));

  const hasDuplicates = !!(Array.isArray(duplicatedDocumentsIds) && duplicatedDocumentsIds.length);

  const handleDownloadClick = useCallback(async() => {
    const link = await dispatch(MainApiActions.fetchAttachmentUrl(attachment, true));

    if (!link) return;
    Files.downloadContent(link);
  }, [attachment, dispatch]);

  useLayoutEffect(() => {
    Promise.all([
      dispatch(CommentsActions.fetchComments(CommentTargetTypes.DOCUMENTS, documentId, true)),
      dispatch(EventsActions.fetchEventsByRoute(MainApiRoutes.DOCUMENTS, documentId))
    ]).then(() => {
      setActivityLoaded(true);
    });
  }, [dispatch, documentId]);
  if (!data) {
    return (
      <SideBar className={Css.listItemDetails}>
        <SideBarHeader titleClassName={Css.title} onCloseClick={onClose}>
          {uiTexts.document}
        </SideBarHeader>
        <Preloader />
      </SideBar>
    );
  }

  return (
    <SideBar className={Css.listItemDetails}>
      <SideBarHeader titleClassName={Css.title} onCloseClick={onClose}>
        <div>
          {extractionStatus
            ? uiTexts.document
            : <>
              {hasDuplicates
                ? <Icons.Copy className={Css.warning} />
                : <Icons.FileText className={recogniseStatus === DocumentRecogniseStatuses.ERROR ? Css.negative : ""} />}
              <span>
                {uiTexts[typeContent] || uiTexts.document}
                {!!originalDocumentId && `: ${originalDocumentId}`}
              </span>
            </>}
        </div>
        {!!attachment?.key && (
          <AwaitingButton
            outline
            className={Css.button}
            onClick={handleDownloadClick}>
            <Icons.DownloadSimple />
          </AwaitingButton>
        )}
      </SideBarHeader>
      <SideBarContent>
        <div className={Css.content}>
          {activityLoaded
            ? (
              <>
                <div className={Css.col}>
                  <DocumentPreview
                    className={Css.preview}
                    error={recogniseStatus === DocumentRecogniseStatuses.ERROR}
                    previewPageMeta={previewPageMeta}
                    hasAttachment={!!attachmentKey}
                    documentPreview={previewFile} />
                </div>
                <div className={classNames(Css.col, Css.activityCol)}>
                  <FormLabel>{uiTexts.activity}</FormLabel>
                  <Activity
                    mobile
                    dataLoadedInitial
                    className={classNames(Css.activity, Css.activityPanel)}
                    contentClassName={Css.activityContent}
                    itemId={documentId}
                    type={CommentTargetTypes.DOCUMENTS}
                    commentsCount={comments} />
                </div>
              </>
            )
            : <Preloader />}
        </div>
      </SideBarContent>
    </SideBar>
  );
};

export default React.memo(ListItemDetails);
